import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, forkJoin, interval, map, switchMap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ApiService } from '../transport/api.service';
import { Endpoints } from '../transport/endpoints';
import { IPaginated } from '../types/http.interface';
import { INotificationFeedItem, NotificationTypeFilter } from '../types/notification.interface';

export type { NotificationTypeFilter } from '../types/notification.interface';

interface INotificationsQueryParams {
  page?: number;
  limit?: number;
  notification_type?: NotificationTypeFilter;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);

  unreadCount$ = new BehaviorSubject<number>(0);
  activeRequestCount$ = new BehaviorSubject<number>(0);

  private pollingSubscription: Subscription | null = null;

  private notificationsBaseUrl(): string {
    const isAdminActor = this.authService.hasAdminProjectsAccessRuntimeSync;
    return isAdminActor ? Endpoints.notifications.adminList : Endpoints.notifications.list;
  }

  getNotifications(params: INotificationsQueryParams = {}): Observable<IPaginated<INotificationFeedItem[]>> {
    const queryParams: Record<string, string | number> = { view: 'notifications' };
    queryParams['notification_type'] = params.notification_type ?? 'all';

    if (params.page != null) {
      queryParams['page'] = params.page;
    }

    if (params.limit != null) {
      queryParams['limit'] = params.limit;
    }

    return this.api.get<IPaginated<INotificationFeedItem[]>>(this.notificationsBaseUrl(), queryParams);
  }

  getUnreadNotifications(params: { page?: number; limit?: number } = {}): Observable<IPaginated<INotificationFeedItem[]>> {
    return this.getNotifications({
      ...params,
      notification_type: 'unread_messages',
    });
  }

  /**
   * @deprecated Use getUnreadNotifications().
   */
  getUnreadProjects(params: { page?: number; limit?: number } = {}): Observable<IPaginated<INotificationFeedItem[]>> {
    return this.getUnreadNotifications(params);
  }

  getActiveRequestsCount(): Observable<number> {
    const requests = [
      this.getNotifications({
        notification_type: 'customer_profile_requests',
        limit: 1,
      }),
      this.getNotifications({
        notification_type: 'special_pricing_requests',
        limit: 1,
      }),
    ];

    if (this.authService.hasAdminProjectsAccessRuntimeSync) {
      requests.push(this.getNotifications({
        notification_type: 'po_review_requests',
        limit: 1,
      }));
    }

    if (this.authService.canAccessBatteryCatalogAdminRuntimeSync) {
      requests.push(this.getNotifications({
        notification_type: 'battery_model_requests',
        limit: 1,
      }));
    }

    return forkJoin(requests).pipe(
      map((responses) => responses.reduce(
        (total, response) => total + response.meta.total,
        0,
      ))
    );
  }

  refreshActiveRequestCount(): void {
    this.getActiveRequestsCount().subscribe({
      next: (total: number) => this.activeRequestCount$.next(total),
      error: () => {}
    });
  }

  /**
   * @deprecated Use refreshActiveRequestCount().
   */
  refreshCount(): void {
    this.refreshActiveRequestCount();
  }

  startPolling(): void {
    if (this.pollingSubscription != null) {
      return;
    }

    this.pollingSubscription = interval(60000).pipe(
      switchMap(() => this.getActiveRequestsCount())
    ).subscribe({
      next: (total: number) => this.activeRequestCount$.next(total),
      error: () => {}
    });
  }

  stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }
}
